declare global {
  interface Window {
    dataLayer: any[]
  }
}

export type GA4Item = {
  item_id: string
  item_name: string
  price: number
  quantity?: number
  item_brand?: string
  item_category?: string
  item_variant?: string
  index?: number
}

export type Promo = {
  promotion_id?: string
  promotion_name: string
  creative_name: string
  creative_slot?: string
  location_id?: number | string
}

export function toGA4Item(
  p: {
    itemId: string | number
    name: string
    price?: string | number
    quantity?: string | number
    variant?: string | null
  },
  idx?: number,
): GA4Item {
  return {
    item_id: String(p.itemId),
    item_name: p.name,
    price: typeof p.price === 'number' ? p.price : parseFloat(p.price ?? '0'),
    ...(p.quantity !== undefined && {
      quantity: typeof p.quantity === 'number' ? p.quantity : parseInt(p.quantity, 10),
    }),
    ...(p.variant && { item_variant: p.variant }),
    ...(idx !== undefined && { index: idx }),
  }
}

export function toPromoItem(input: {
  name: string
  id?: string | number | null
  creative: string
  position: number
}): Promo {
  return {
    promotion_id: input.id != null ? String(input.id) : '',
    promotion_name: input.name,
    creative_name: input.creative,
    creative_slot: '',
    location_id: input.position,
  }
}

// Legacy PHP built variant strings as `implode(', ', array_filter([modification_name, item_name]))`
// (e.g. "Blue, M") — this mirrors that so item_variant stays consistent across every
// tracking call site instead of each one composing it slightly differently.
export function toGA4Variant(...parts: (string | null | undefined)[]): string | undefined {
  return parts.filter(Boolean).join(', ') || undefined
}

export function useDataLayer() {
  function debug(_label: string, _payload?: unknown) {}

  function push(payload: Record<string, unknown>) {
    if (import.meta.server) return
    window.dataLayer = window.dataLayer || []
    window.dataLayer.push(payload)
  }

  function clearEcommerce() {
    debug('clearing ecommerce object before next ecommerce event')
    push({ ecommerce: null })
  }

  function trackProductView(item: GA4Item) {
    debug('trackProductView called', item)
    clearEcommerce()
    push({ event: 'view_item', ecommerce: { items: [item] } })
  }

  function trackProductListView(items: GA4Item[], listName?: string) {
    debug('trackProductListView called', { listName, items })
    clearEcommerce()
    push({ event: 'view_item_list', ecommerce: { item_list_name: listName, items } })
  }

  function trackSelectItem(item: GA4Item, listName?: string) {
    debug('trackSelectItem called', { listName, item })
    clearEcommerce()
    push({ event: 'select_item', ecommerce: { item_list_name: listName, items: [item] } })
  }

  function trackAddToCart(item: GA4Item) {
    debug('trackAddToCart called', item)
    clearEcommerce()
    push({ event: 'add_to_cart', ecommerce: { items: [item] } })
  }

  function trackRemoveFromCart(item: GA4Item) {
    debug('trackRemoveFromCart called', item)
    clearEcommerce()
    push({ event: 'remove_from_cart', ecommerce: { items: [item] } })
  }

  function trackBeginCheckout(items: GA4Item[], coupon?: string) {
    debug('trackBeginCheckout called', { coupon, items })
    clearEcommerce()
    push({ event: 'begin_checkout', ecommerce: { coupon, items } })
  }

  // begin_checkout marks commitment to checkout, not cart viewing, so it fires at the first
  // real step — prelogin for guests, delivery for already-logged-in users — never on the cart
  // page. Dedup per checkout session via the cart hash: a guest passing prelogin → delivery
  // fires exactly once, and a fresh cart (new hash) naturally re-arms the event.
  function trackBeginCheckoutOnce(items: GA4Item[], coupon: string | undefined, cartHash: string | null) {
    if (import.meta.server) return
    const key = `begin_checkout:${cartHash ?? 'nohash'}`
    if (sessionStorage.getItem(key)) return
    sessionStorage.setItem(key, '1')
    trackBeginCheckout(items, coupon)
  }

  function trackAddShippingInfo(items: GA4Item[], shippingTier?: string) {
    debug('trackAddShippingInfo called', { shippingTier, items })
    clearEcommerce()
    push({ event: 'add_shipping_info', ecommerce: { shipping_tier: shippingTier, items } })
  }

  function trackAddPaymentInfo(items: GA4Item[], paymentType?: string) {
    debug('trackAddPaymentInfo called', { paymentType, items })
    clearEcommerce()
    push({ event: 'add_payment_info', ecommerce: { payment_type: paymentType, items } })
  }

  function trackPurchase(payload: {
    orderId: string
    revenue: number
    tax?: number
    shipping?: number
    coupon?: string
    items: GA4Item[]
  }) {
    debug('trackPurchase called', payload)
    clearEcommerce()
    push({
      event: 'purchase',
      ecommerce: {
        transaction_id: payload.orderId,
        value: payload.revenue,
        tax: payload.tax,
        shipping: payload.shipping,
        coupon: payload.coupon,
        items: payload.items,
      },
    })
  }

  function trackPromotionView(promos: Promo | Promo[]) {
    const items = Array.isArray(promos) ? promos : [promos]
    debug('trackPromotionView called', items)
    clearEcommerce()
    push({ event: 'view_promotion', ecommerce: { items } })
  }

  function trackSelectPromotion(promo: Promo) {
    debug('trackSelectPromotion called', promo)
    clearEcommerce()
    push({ event: 'select_promotion', ecommerce: { items: [promo] } })
  }

  function trackLogin(method: string, userId?: number | string) {
    debug('trackLogin called', { method, userId })
    push({ event: 'login', method, user_id: userId })
  }

  function trackSignUp(method: string, userId?: number | string) {
    debug('trackSignUp called', { method, userId })
    push({ event: 'sign_up', method, user_id: userId })
  }

  function trackRecommendedProductView(item: GA4Item) {
    debug('trackRecommendedProductView called', item)
    push({ event: 'recommended_product_view', ecommerce: { items: [item] } })
  }

  return {
    push,
    trackProductView,
    trackProductListView,
    trackSelectItem,
    trackAddToCart,
    trackRemoveFromCart,
    trackBeginCheckout,
    trackBeginCheckoutOnce,
    trackAddShippingInfo,
    trackAddPaymentInfo,
    trackPurchase,
    trackPromotionView,
    trackSelectPromotion,
    trackLogin,
    trackSignUp,
    trackRecommendedProductView,
  }
}
